// src/components/SubMenuToolbar.js
import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Tooltip,
  Divider,
  Stack,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  FormatAlignCenter as FormatAlignCenterIcon,
  Deselect as DeselectIcon,
  AutoAwesome as AutoAwesomeIcon,
  GroupsOutlined as GroupsOutlinedIcon,
  MoreVert as MoreVertIcon,
} from "@mui/icons-material";
import ImageIcon from "@mui/icons-material/Image";
import StreetviewIcon from "@mui/icons-material/Streetview";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import BreadcrumbsComponent from "./Breadcrumbs";
import StreetViewSelector from "./StreetViewSelector";
import DesignHelpPopover from "./DesignHelpPopover";
import SharePopover from "./SharePopover";
import useGoogleMaps from "../hooks/useGoogleMaps";
import PDFDownloader from "./PDFDownloader";

import { useData } from "../context/DataContext";
import { useCanvas } from "../context/CanvasContext";
import { useMediaQuery } from "@mui/material";
import Compressor from "compressorjs";

const SubMenuToolbar = () => {
  const {
    setSnackbar,
    backgroundImage,
    setBackgroundImage,
    lockBackgroundImage,
    setLockBackgroundImage,
  } = useData();

  const { canvas } = useCanvas();
  const isMobile = useMediaQuery("(max-width:767px)");
  const fileInputRef = useRef(null);
  const patternRef = useRef(null);

  // BUTTON STATES
  const [isTextActionDisabled, setIsTextActionDisabled] = useState(true);
  const [isDeleteDisabled, setIsDeleteDisabled] = useState(true);
  const [isDeselectDisabled, setIsDeselectDisabled] = useState(true);
  const [isDesignHelpDisabled, setIsDesignHelpDisabled] = useState(true);
  const [isShareDisabled, setIsShareDisabled] = useState(true);
  const [streetViewOpen, setStreetViewOpen] = useState(false);
  const isGoogleMapsLoaded = useGoogleMaps();

  // POPOVER STATES
  const [showDesignHelpPopover, setShowDesignHelpPopover] = useState(false);
  const [showSharePopover, setShowSharePopover] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  // CANVAS LISTENERS
  useEffect(() => {
    if (!canvas) return;

    const updateStates = () => {
      const active = canvas.getActiveObject();
      const hasObjects = canvas.getObjects().length > 0;
      const isTextSelected = active && active.type === "i-text";

      console.log("[SubMenuToolbar] Selection changed", {
        activeObject: active?.type,
        isTextSelected,
        hasObjects,
      });

      setIsTextActionDisabled(!isTextSelected);
      setIsDeleteDisabled(!isTextSelected);
      setIsDeselectDisabled(!isTextSelected);
      setIsDesignHelpDisabled(!hasObjects);
      setIsShareDisabled(!hasObjects);
    };

    // Listen to ALL selection events
    canvas.on("selection:created", updateStates);
    canvas.on("selection:updated", updateStates);
    canvas.on("selection:cleared", updateStates);

    // Initial state
    updateStates();

    return () => {
      canvas.off("selection:created", updateStates);
      canvas.off("selection:updated", updateStates);
      canvas.off("selection:cleared", updateStates);
    };
  }, [canvas]); // ← MUST depend on canvas

  // ACTION HANDLERS
  const handleDelete = () => {
    const obj = canvas.getActiveObject();
    if (obj?.type === "i-text") {
      canvas.remove(obj);
      canvas.discardActiveObject();
      canvas.requestRenderAll();
      setSnackbar({
        open: true,
        message: "Text deleted.",
        severity: "success",
      });
    }
  };

  const handleCenter = () => {
    const obj = canvas.getActiveObject();
    if (obj?.type === "i-text") {
      obj.set({ left: canvas.getWidth() / 2, originX: "center" });
      obj.setCoords();
      canvas.requestRenderAll();
    }
  };

  const handleDeselect = () => {
    canvas.discardActiveObject();
    canvas.requestRenderAll();
  };

  const handleImageUpload = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) {
      setSnackbar({
        open: true,
        message: "No file selected.",
        severity: "warning",
      });
      console.log("SubMenuToolbar: No file selected");
      return;
    }

    const allowedTypes = ["image/png", "image/jpeg", "image/jpg"];
    if (!allowedTypes.includes(file.type)) {
      setSnackbar({
        open: true,
        message: "Only PNG and JPEG images are supported.",
        severity: "error",
      });
      console.log("SubMenuToolbar: Invalid file type", file.type);
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setSnackbar({
        open: true,
        message: "Image size exceeds 5MB limit.",
        severity: "error",
      });
      console.log("SubMenuToolbar: File size exceeds 5MB", file.size);
      return;
    }

    new Compressor(file, {
      quality: 0.8,
      maxWidth: 1524,
      maxHeight: 500,
      success(result) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const dataUrl = e.target.result;
          if (!dataUrl.startsWith("data:image/")) {
            console.error(
              "SubMenuToolbar: Invalid Data URL",
              dataUrl.slice(0, 50)
            );
            setSnackbar({
              open: true,
              message: "Invalid image format",
              severity: "error",
            });
            return;
          }
          console.log("SubMenuToolbar: Data URL", {
            length: dataUrl.length,
            prefix: dataUrl.slice(0, 50),
          });
          setBackgroundImage(dataUrl);
          setSnackbar({
            open: true,
            message: "Background image uploaded successfully!",
            severity: "success",
          });
        };
        reader.onerror = () => {
          console.error("SubMenuToolbar: FileReader error");
          setSnackbar({
            open: true,
            message: "Failed to read image",
            severity: "error",
          });
        };
        reader.readAsDataURL(result);
      },
      error(err) {
        console.error("SubMenuToolbar: Compressor error", err);
        setSnackbar({
          open: true,
          message: "Failed to process image",
          severity: "error",
        });
      },
    });

    event.target.value = "";
  };

  const handleRemoveBackground = () => {
    setBackgroundImage(null);
    setSnackbar({
      open: true,
      message: "Background image removed.",
      severity: "success",
    });
    console.log("SubMenuToolbar: Remove background triggered");
  };

  const handleToggleLock = () => {
    setLockBackgroundImage(!lockBackgroundImage);
    setSnackbar({
      open: true,
      message: `Background image ${
        lockBackgroundImage ? "unlocked" : "locked"
      }.`,
      severity: "info",
    });
    console.log("SubMenuToolbar: Toggled lock state", {
      locked: !lockBackgroundImage,
    });
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        p: { xs: 0.5, sm: 1 },
        backgroundColor: "background.paper",
        borderBottom: "1px solid",
        borderColor: "divider",
      }}
    >
      {/* BREADCRUMBS — HIDDEN ON MOBILE */}
      <Box
        sx={{
          ml: { xs: 1, sm: 2, md: 12 },
          display: { xs: "none", sm: "block" },
        }}
      >
        <BreadcrumbsComponent />
      </Box>

      {/* TOOLBAR */}
      <Stack direction="row" spacing={2} sx={{ mr: { xs: 1, sm: 2, md: 12 } }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {/* ALWAYS VISIBLE */}
          <Tooltip title="Deselect all objects">
            <span>
              <Button
                onClick={handleDeselect}
                disabled={isDeselectDisabled}
                variant="outlined"
                size="small"
                startIcon={<DeselectIcon />}
                sx={{ textTransform: "none", minWidth: 64 }}
              >
                Deselect
              </Button>
            </span>
          </Tooltip>

          <Tooltip title="Center selected text">
            <span>
              <Button
                onClick={handleCenter}
                disabled={isDeleteDisabled}
                variant="outlined"
                size="small"
                startIcon={<FormatAlignCenterIcon />}
                sx={{ textTransform: "none", minWidth: 64 }}
              >
                Center
              </Button>
            </span>
          </Tooltip>

          <Tooltip title="Delete selected text">
            <span>
              <Button
                onClick={handleDelete}
                disabled={isDeleteDisabled}
                variant="outlined"
                color="error"
                size="small"
                startIcon={<DeleteIcon />}
                sx={{ textTransform: "none", minWidth: 64 }}
              >
                Delete
              </Button>
            </span>
          </Tooltip>
          <Divider orientation="vertical" sx={{ mx: 2 }} flexItem />
          <Box
            sx={{
              display: { xs: "none", sm: "flex" },
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography
              component="span"
              sx={{ color: "primary.main", fontWeight: 500 }}
            >
              Background Tools:
            </Typography>
            <Tooltip title="View Streetview for an address">
              <span>
                <IconButton
                  onClick={() => setStreetViewOpen(true)}
                  aria-label="View Streetview"
                  sx={{ textTransform: "none" }}
                  disabled={!isGoogleMapsLoaded}
                >
                  <StreetviewIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Upload an image to set as canvas background">
              <span>
                <IconButton
                  onClick={handleImageUpload}
                  aria-label="Upload background image"
                  sx={{ textTransform: "none" }}
                >
                  <ImageIcon />
                </IconButton>
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  accept="image/*"
                  onChange={handleFileChange}
                />
              </span>
            </Tooltip>
            <Tooltip
              title={
                lockBackgroundImage
                  ? "Unlock background image"
                  : "Lock background image"
              }
            >
              <span>
                <IconButton
                  onClick={handleToggleLock}
                  disabled={!backgroundImage}
                  aria-label={
                    lockBackgroundImage
                      ? "Unlock background image"
                      : "Lock background image"
                  }
                >
                  {lockBackgroundImage ? <LockIcon /> : <LockOpenIcon />}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Remove the background image">
              <span>
                <IconButton
                  onClick={handleRemoveBackground}
                  disabled={!backgroundImage}
                  aria-label="Remove the background image"
                  sx={{ textTransform: "none" }}
                >
                  <DeleteOutlineIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          <Divider orientation="vertical" sx={{ mx: 2 }} flexItem />

          {/* MOBILE: COLLAPSE INTO MORE MENU */}
          {isMobile ? (
            <>
              <IconButton onClick={handleMenuOpen} size="small">
                <MoreVertIcon />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                PaperProps={{ sx: { mt: 1 } }}
              >
                <MenuItem
                  onClick={() => {
                    setShowDesignHelpPopover(true);
                    handleMenuClose();
                  }}
                  disabled={isDesignHelpDisabled}
                >
                  <ListItemIcon>
                    <AutoAwesomeIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Design Help</ListItemText>
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setShowSharePopover(true);
                    handleMenuClose();
                  }}
                  disabled={isShareDisabled}
                >
                  <ListItemIcon>
                    <GroupsOutlinedIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Share</ListItemText>
                </MenuItem>
              </Menu>
            </>
          ) : (
            <>
              <Divider orientation="vertical" flexItem />
              <Tooltip title="Request professional design help">
                <span>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AutoAwesomeIcon />}
                    disabled={isDesignHelpDisabled}
                    onClick={() => setShowDesignHelpPopover(true)}
                    sx={{ textTransform: "none" }}
                  >
                    Design Help
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title="Share your design">
                <span>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<GroupsOutlinedIcon />}
                    disabled={isShareDisabled}
                    onClick={() => setShowSharePopover(true)}
                    sx={{ textTransform: "none" }}
                  >
                    Share
                  </Button>
                </span>
              </Tooltip>
              <PDFDownloader />
            </>
          )}
        </Box>
      </Stack>

      {/* POPOVERS */}
      <DesignHelpPopover
        canvas={canvas}
        open={showDesignHelpPopover}
        onClose={() => setShowDesignHelpPopover(false)}
      />
      <SharePopover
        canvas={canvas}
        open={showSharePopover}
        onClose={() => setShowSharePopover(false)}
      />
      <StreetViewSelector
        open={streetViewOpen}
        onClose={() => setStreetViewOpen(false)}
        onImageSelect={(imageUrl) => {
          setBackgroundImage(imageUrl);
          setSnackbar({
            open: true,
            message: "Street View image added to canvas!",
            severity: "success",
          });
          setStreetViewOpen(false);
        }}
      />
    </Box>
  );
};

export default SubMenuToolbar;
